/**
 * In-memory fallback CacheStore.
 *
 * 当 IndexedDB 不可用时（隐私模式 / 配额满 / 旧浏览器）使用本实现。
 * 功能完全一致但只在当前页面生命周期内有效（F5 即丢）。
 */

import type { CacheStore, CacheStoreEntry, CacheStoreName } from './types';

const _stores: Record<CacheStoreName, Map<string, unknown>> = {
  lookups: new Map(),
  meta: new Map(),
};

export const memoryStore: CacheStore = {
  async get<T = unknown>(store: CacheStoreName, key: string): Promise<T | undefined> {
    return _stores[store].get(key) as T | undefined;
  },

  async set<T = unknown>(store: CacheStoreName, key: string, value: T): Promise<void> {
    _stores[store].set(key, value);
  },

  async setMany(entries) {
    for (const e of entries) {
      _stores[e.store].set(e.key, e.value);
    }
  },

  async delete(store: CacheStoreName, key: string): Promise<void> {
    _stores[store].delete(key);
  },

  async clear(store: CacheStoreName): Promise<void> {
    _stores[store].clear();
  },

  async getAll<T = unknown>(store: CacheStoreName): Promise<Array<CacheStoreEntry<T>>> {
    return Array.from(_stores[store].entries()).map(([key, value]) => ({
      key,
      value: value as T,
    }));
  },

  async deleteMatching(store: CacheStoreName, predicate: (key: string) => boolean): Promise<number> {
    let count = 0;
    for (const key of Array.from(_stores[store].keys())) {
      if (predicate(key)) {
        _stores[store].delete(key);
        count += 1;
      }
    }
    return count;
  },
};

/**
 * Test-only：清空所有 store。
 */
export function _resetForTests(): void {
  for (const store of Object.keys(_stores) as CacheStoreName[]) {
    _stores[store].clear();
  }
}
